import { IconLink } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { UTM as UTMType } from 'typings'
import { useFormStateContext } from '../../../../providers'
import { useDrawerState } from '../../../Drawer'
import { useConditions } from '../useConditions'
import { Editor } from './Editor'
import { UTMView } from './View'
import { useUTMsSearchParams } from './hooks/useUTMsSearchParams'
import { messages } from './messages'

interface Props {
  name: string
}

export function UTM(props: Props) {
  const { formatMessage: i18n } = useIntl()
  const { name } = props
  const { form } = useFormStateContext()
  const config: UTMType | undefined = useWatch({ form, name })
  const editorState = useDrawerState()

  const handleRemove = () => {
    form.setValue(name, undefined)
  }

  const updateUtms = useCallback(
    (values: UTMType, clear = true) => {
      const hasUtms = Object.values(values).some((x) => x.length)
      if (hasUtms) {
        return form.setValue(name, values)
      }
      if (clear) {
        form.setValue(name, undefined)
      }
    },
    [form, name]
  )

  useUTMsSearchParams({ update: updateUtms })

  const handleSave = (values: UTMType) => updateUtms(values)

  const initialEditorConfig = useMemo<UTMType>(
    () => config ?? { utmSource: [], utmCampaign: [] },
    [config]
  )

  useConditions({
    name,
    icon: <IconLink />,
    onEdit: editorState.show,
    label: i18n(messages.title),
  })

  return (
    <>
      <UTMView
        onEdit={editorState.show}
        onRemove={handleRemove}
        values={config}
      />
      <Editor
        state={editorState}
        initialConfig={initialEditorConfig}
        onSave={handleSave}
      />
    </>
  )
}
